import { useEffect } from 'react';
import { useParams, Link } from 'react-router';
import { useCapabilityDetail } from '@/hooks/use-entity-detail';
import { usePageTitle } from '@/hooks/use-page-title';
import { useHeaderAction } from '@/lib/header-action-context';
import { capabilityColors, capabilityTitleIconBackground, getCapabilityColors } from '@/lib/capability-colors';
import { capabilityDetailPath } from '@/types';
import { Card } from '@/components/ui/card';
import {
    Server,
    Circle,
    Clock,
    Cpu,
    Activity,
    HardDrive,
    Loader2,
    ExternalLink,
    AlertTriangle,
} from 'lucide-react';

interface WorkerCapabilityData {
    id?: string;
    name?: string;
    description?: string | null;
    type?: string;
    status?: string;
    fqcn?: string;
    app_key?: string;
    created?: string;
    modified?: string;
    config?: {
        capabilities_hosted?: Array<{ name: string; type: string }>;
        [key: string]: unknown;
    };
    ext?: {
        host?: string;
        pid?: number;
        started_at?: string;
        [key: string]: unknown;
    };
}

const statusColors: Record<string, { dot: string; bg: string; text: string }> = {
    online: { dot: 'text-emerald-500', bg: 'bg-emerald-50', text: 'text-emerald-700' },
    draining: { dot: 'text-amber-500', bg: 'bg-amber-50', text: 'text-amber-700' },
    offline: { dot: 'text-slate-400', bg: 'bg-slate-50', text: 'text-slate-500' },
};

function formatRelativeTime(dateStr: string): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const seconds = Math.floor(diff / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
}

function formatUptime(startedAt: string): string {
    const diff = Date.now() - new Date(startedAt).getTime();
    const hours = Math.floor(diff / 3_600_000);
    const minutes = Math.floor((diff % 3_600_000) / 60_000);
    if (hours > 24) {
        const days = Math.floor(hours / 24);
        return `${days}d ${hours % 24}h`;
    }
    return `${hours}h ${minutes}m`;
}

export default function WorkerDetailPage() {
    const { appKey, name } = useParams<{ appKey: string; name: string }>();
    const { data, isLoading } = useCapabilityDetail(appKey!, name!);
    const colors = capabilityColors.worker;
    const { setHeaderTitle } = useHeaderAction();

    const workerData = data as WorkerCapabilityData | undefined;

    usePageTitle(`Worker - ${workerData?.name || name || ''}`);

    useEffect(() => {
        if (!workerData?.name) {
            setHeaderTitle(null);
            return;
        }
        const extHost = workerData?.ext?.host;
        const extPid = workerData?.ext?.pid;
        const status = workerData.status ?? 'offline';
        const sc = statusColors[status] ?? statusColors.offline;
        setHeaderTitle({
            icon: (
                <div
                    className="flex h-7 w-7 items-center justify-center rounded-md"
                    style={capabilityTitleIconBackground(colors.hex, true)}
                >
                    <Server size={16} className={colors.text} />
                </div>
            ),
            title: workerData.name,
            subtitle: extHost ? `${extHost}${extPid ? `:${extPid}` : ''}` : undefined,
            intense: true,
            badges: (
                <span
                    className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${sc.bg} ${sc.text}`}
                >
                    <Circle size={8} className={`fill-current ${sc.dot}`} />
                    {status}
                </span>
            ),
        });
        return () => setHeaderTitle(null);
    }, [workerData?.name, workerData?.status, workerData?.ext?.host, workerData?.ext?.pid, colors, setHeaderTitle]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center p-12">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
        );
    }

    if (!data) {
        return (
            <div className="p-6">
                <p className="text-slate-600">Worker not found.</p>
            </div>
        );
    }

    const status = workerData?.status ?? 'offline';
    const sc = statusColors[status] ?? statusColors.offline;
    const hosted = workerData?.config?.capabilities_hosted ?? [];
    const ext = workerData?.ext ?? {};

    const hostedByType = hosted.reduce<Record<string, Array<{ name: string; type: string }>>>(
        (acc, cap) => {
            const group = acc[cap.type] ?? [];
            group.push(cap);
            acc[cap.type] = group;
            return acc;
        },
        {},
    );

    return (
        <div className="p-6 max-w-5xl space-y-6">
            {/* Summary */}
            {workerData?.description && (
                <Card className={`p-6 border-l-4 ${colors.border}`}>
                    <p className="text-sm text-slate-600">{workerData.description}</p>
                </Card>
            )}

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="p-4 flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50">
                        <Activity size={20} className="text-emerald-600" />
                    </div>
                    <div>
                        <p className="text-xs text-slate-500">Status</p>
                        <p className={`font-semibold capitalize ${sc.text}`}>{status}</p>
                    </div>
                </Card>

                <Card className="p-4 flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50">
                        <HardDrive size={20} className="text-blue-600" />
                    </div>
                    <div>
                        <p className="text-xs text-slate-500">Capabilities Hosted</p>
                        <p className="font-semibold text-slate-900">{hosted.length}</p>
                    </div>
                </Card>

                <Card className="p-4 flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-violet-50">
                        <Cpu size={20} className="text-violet-600" />
                    </div>
                    <div>
                        <p className="text-xs text-slate-500">Uptime</p>
                        <p className="font-semibold text-slate-900">
                            {ext.started_at ? formatUptime(ext.started_at) : '—'}
                        </p>
                    </div>
                </Card>

                <Card className="p-4 flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-50">
                        <Clock size={20} className="text-amber-600" />
                    </div>
                    <div>
                        <p className="text-xs text-slate-500">Last Heartbeat</p>
                        <p className="font-semibold text-slate-900">
                            {workerData?.modified ? formatRelativeTime(workerData.modified) : '—'}
                        </p>
                    </div>
                </Card>
            </div>

            {/* Worker Identity */}
            <Card className="p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Worker Identity</h3>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                    <div>
                        <dt className="text-slate-500">Name</dt>
                        <dd className="font-mono text-slate-900">{workerData?.name ?? '—'}</dd>
                    </div>
                    <div>
                        <dt className="text-slate-500">Host</dt>
                        <dd className="font-mono text-slate-900">{ext.host ?? '—'}</dd>
                    </div>
                    <div>
                        <dt className="text-slate-500">PID</dt>
                        <dd className="font-mono text-slate-900">{ext.pid ?? '—'}</dd>
                    </div>
                    <div>
                        <dt className="text-slate-500">Started At</dt>
                        <dd className="text-slate-900">
                            {ext.started_at
                                ? new Date(ext.started_at).toLocaleString()
                                : '—'}
                        </dd>
                    </div>
                    {workerData?.created && (
                        <div>
                            <dt className="text-slate-500">Registered</dt>
                            <dd className="text-slate-900">
                                {new Date(workerData.created).toLocaleString()}
                            </dd>
                        </div>
                    )}
                    {workerData?.modified && (
                        <div>
                            <dt className="text-slate-500">Last Seen</dt>
                            <dd className="text-slate-900">
                                {new Date(workerData.modified).toLocaleString()}
                            </dd>
                        </div>
                    )}
                </dl>
            </Card>

            {/* Hosted Capabilities */}
            <Card className="p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">
                    Hosted Capabilities
                    <span className="ml-2 text-sm font-normal text-slate-500">
                        ({hosted.length})
                    </span>
                </h3>

                {hosted.length === 0 ? (
                    <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                        <AlertTriangle size={18} className="text-amber-600" />
                        <p className="text-sm text-amber-700">
                            This worker is not hosting any capabilities.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {Object.entries(hostedByType)
                            .sort(([a], [b]) => a.localeCompare(b))
                            .map(([type, caps]) => {
                                const typeColors = getCapabilityColors(type);
                                const TypeIcon = typeColors.icon;
                                return (
                                    <div key={type}>
                                        <div className="flex items-center gap-2 mb-2">
                                            <TypeIcon size={14} className={typeColors.text} />
                                            <h4 className="text-sm font-semibold text-slate-700 capitalize">
                                                {type}s
                                            </h4>
                                            <span className="text-xs text-slate-400">
                                                ({caps.length})
                                            </span>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                            {caps.map((cap) => (
                                                <Link
                                                    key={cap.name}
                                                    to={
                                                        appKey
                                                            ? capabilityDetailPath(appKey, cap.type, cap.name)
                                                            : '#'
                                                    }
                                                    className={`flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm transition-colors hover:bg-slate-50 hover:border-slate-300 border-l-2 ${typeColors.border}`}
                                                >
                                                    <TypeIcon size={14} className={typeColors.text} />
                                                    <span className="font-mono text-slate-700 truncate flex-1">
                                                        {cap.name}
                                                    </span>
                                                    <ExternalLink size={12} className="text-slate-400 shrink-0" />
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                    </div>
                )}
            </Card>
        </div>
    );
}
